import * as React from "react";
import { format, startOfWeek, endOfWeek, isSameDay } from "date-fns";
import { CalendarIcon } from "lucide-react";
import { DateRange } from "react-day-picker";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";

interface WeekPickerProps {
    date: { from: Date; to: Date };
    onDateChange: (date: { from: Date; to: Date }) => void;
    className?: string;
}

export function WeekPicker({
    date,
    onDateChange,
    className,
}: WeekPickerProps) {
    const [open, setOpen] = useState(false);

    // When a day is clicked, select the entire week
    const handleDayClick = (day: Date) => {
        const range = {
            from: startOfWeek(day, { weekStartsOn: 1 }), // Monday start
            to: endOfWeek(day, { weekStartsOn: 1 }),
        };
        onDateChange(range);
        setOpen(false); // Close on selection
    };

    // Custom formatted display text
    const formatDisplay = () => {
        if (!date?.from || !date?.to) return "Select week";
        return `${format(date.from, "MMM d")} - ${format(date.to, "MMM d, yyyy")}`;
    };

    return (
        <div className={cn("grid gap-2", className)}>
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                    <Button
                        id="date"
                        variant={"outline"}
                        className={cn(
                            "justify-start text-left font-normal",
                            !date && "text-muted-foreground",
                            className
                        )}
                    >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {formatDisplay()}
                    </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="end">
                    <Calendar
                        initialFocus
                        mode="range"
                        defaultMonth={new Date()}
                        selected={date}
                        onDayClick={handleDayClick}
                        numberOfMonths={1}
                        weekStartsOn={1}
                        modifiers={{
                            // Highlight the selected week logic is handled by 'selected={date}' 
                            // since we set the range to the full week.
                        }}
                        // Disable default range selection behavior by overriding onSelect
                        onSelect={() => { }}
                    />
                </PopoverContent>
            </Popover>
        </div>
    );
}

import { useState } from "react";
